import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Mic, MonitorSpeaker, ShieldCheck, Sparkles, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../Auth';
import { useMeetingRecorder } from './MeetingRecorderContext';
import { recordingSupport } from './recorderEngine';
import ScopePicker, { SCOPE_KINDS, scopeKey, scopeLabel, useScopeOptions } from './ScopePicker';

const defaultTitle = () => `Réunion du ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;

const SourceCard = ({ active, disabled, icon: Icon, title, text, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-1 items-start gap-3 rounded-xl border p-3 text-left transition ${
      active ? 'border-rose-300 bg-rose-50 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
    } disabled:cursor-not-allowed disabled:opacity-50`}
  >
    <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? 'text-rose-600' : 'text-slate-400'}`} />
    <span>
      <span className="block text-sm font-semibold text-slate-900">{title}</span>
      <span className="block text-xs text-slate-500">{text}</span>
    </span>
  </button>
);

const StartMeetingDialog = () => {
  const { dialog, closeDialog, start, phase } = useMeetingRecorder();
  const { user } = useAuth();
  const support = useMemo(() => recordingSupport(), []);
  const options = useScopeOptions();
  const [employees, setEmployees] = useState([]);
  // Projet, client ou programme ({kind, id, name, reason}), ou { kind: 'none' } : sans projet ; null : à choisir.
  const [scope, setScope] = useState(null);
  const [title, setTitle] = useState(defaultTitle);
  const [source, setSource] = useState('micro');
  const [participantIds, setParticipantIds] = useState([]);
  const [hideTranscript, setHideTranscript] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    if (!dialog) return;
    setScope(dialog.projectId ? { kind: 'project', id: dialog.projectId, name: null } : null);
    setTitle(defaultTitle());
    setParticipantIds([]);
    setHideTranscript(false);
    setCalendar(null);
    apiService.get('/employees').then((rows) => setEmployees((rows || []).filter((e) => e.is_active))).catch(() => {});
    // Réunion en cours dans l'agenda Google : titre, participants et portée préremplis (avec la raison).
    apiService.get('/meetings/calendar/current').then((current) => {
      setCalendar(current);
      if (!current?.event) return;
      if (current.event.title) setTitle(current.event.title);
      setParticipantIds(current.participant_ids || []);
      // Sans écraser un choix fait avant la réponse de l'agenda.
      if (!dialog.projectId && current.suggested) setScope((picked) => picked || current.suggested);
    }).catch(() => setCalendar(null));
  }, [dialog]);

  const linkCalendar = async () => {
    const { authorization_url: url } = await apiService.startGoogleCalendarAuth(window.location.pathname + window.location.search);
    window.location.href = url;
  };

  const me = employees.find((e) => e.email === user?.email);
  // Projet ouvert depuis sa page : son nom vient de la liste des choix.
  const chosen = scope?.kind === 'project' && !scope.name
    ? { ...scope, name: options?.projects?.find((p) => p.id === scope.id)?.name || '…' } : scope;
  const suggestions = calendar?.suggestions || [];
  const others = suggestions.filter((s) => scopeKey(s) !== scopeKey(chosen));
  const peopleMatches = employees
    .filter((e) => e.id !== me?.id && !participantIds.includes(e.id))
    .filter((e) => e.name.toLowerCase().includes(peopleQuery.toLowerCase()))
    .slice(0, 6);

  if (!dialog) return null;

  const canStart = Boolean(chosen) && title.trim() && support[source] && phase !== 'starting';
  const submit = async (event) => {
    event.preventDefault();
    if (!canStart) return;
    closeDialog();
    const id = (kind) => (chosen.kind === kind ? chosen.id : null);
    await start({ projectId: id('project'), clientId: id('client'), programmeId: id('programme'),
      projectName: scopeLabel(chosen), title: title.trim(), source,
      participantIds: participantIds.filter((pid) => pid !== me?.id), calendar: calendar?.event ? calendar : null,
      hideTranscript });
  };
  const ChosenIcon = chosen ? SCOPE_KINDS[chosen.kind].icon : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4">
      <form onSubmit={submit} className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Enregistrer une réunion</h2>
            <p className="text-xs text-slate-500">Transcrite sur le serveur d'Innowide, puis l'audio est détruit.</p>
          </div>
          <button type="button" onClick={closeDialog} className="rounded-full p-1 text-slate-400 hover:bg-slate-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {calendar?.event && (
          <p className="mb-4 flex items-start gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-800">
            <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              D'après ton agenda : « {calendar.event.title} ».
              {calendar.external_attendees?.length > 0 && ` Invités externes : ${calendar.external_attendees.map((a) => a.name).join(', ')}.`}
            </span>
          </p>
        )}
        {calendar && !calendar.connected && (
          <button type="button" onClick={linkCalendar}
            className="mb-4 flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-left text-xs text-slate-600 hover:bg-slate-50">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            Lier mon agenda Google (réglage de ton profil) : titre et participants seront préremplis.
          </button>
        )}

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Projet, client ou programme</label>
        {chosen ? (
          <div className="mb-4">
            <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2">
              <span className="min-w-0 text-sm text-slate-900">
                <span className="flex items-center gap-1.5 font-medium">
                  <ChosenIcon className={`h-4 w-4 shrink-0 ${SCOPE_KINDS[chosen.kind].tone}`} />
                  <span className="truncate">{scopeLabel(chosen)}</span>
                </span>
                {chosen.kind === 'none' && (
                  <span className="block text-xs text-slate-500">Liée à toi et aux collègues présents ; rattachable plus tard à un projet.</span>
                )}
                {(chosen.kind === 'client' || chosen.kind === 'programme') && (
                  <span className="flex items-start gap-1 text-xs text-slate-500">
                    <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-violet-500" />
                    Nexus dira dans la synthèse quels projets la réunion concerne et ce qui revient à chacun.
                  </span>
                )}
                {chosen.reason && <span className="block text-xs text-blue-700">D'après l'agenda : {chosen.reason}.</span>}
              </span>
              <button type="button" onClick={() => setScope(null)} className="shrink-0 text-xs text-blue-600 hover:underline">Changer</button>
            </div>
            {others.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                Ou :
                {others.map((s) => {
                  const Icon = SCOPE_KINDS[s.kind].icon;
                  return (
                    <button key={scopeKey(s)} type="button" onClick={() => setScope(s)} title={s.reason}
                      className={`flex items-center gap-1 rounded-full px-2 py-0.5 hover:opacity-80 ${SCOPE_KINDS[s.kind].chip}`}>
                      <Icon className="h-3 w-3" />{scopeLabel(s)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <ScopePicker onPick={setScope} suggestions={suggestions} allowNone />
          </div>
        )}

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Titre</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
          className="mb-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-300" />

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
        <div className="mb-1 flex flex-col gap-2 sm:flex-row">
          <SourceCard active={source === 'micro'} disabled={!support.micro} icon={Mic} onClick={() => setSource('micro')}
            title="En présentiel" text="Le micro de cet appareil capte la salle." />
          <SourceCard active={source === 'visio'} disabled={!support.visio} icon={MonitorSpeaker} onClick={() => setSource('visio')}
            title="Visio dans le navigateur" text={support.visio ? 'Son de l’onglet Meet/Teams + ton micro.' : 'Non disponible sur cet appareil.'} />
        </div>
        {source === 'visio' && (
          <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Au lancement, choisis l'onglet de la visio et coche <strong>« Partager l'audio de l'onglet »</strong>.
          </p>
        )}

        <label className="mb-1 mt-3 block text-xs font-semibold uppercase tracking-wide text-slate-500">Participants</label>
        <div className="mb-1 flex flex-wrap gap-1.5">
          {me && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{me.name} (toi)</span>}
          {participantIds.filter((id) => id !== me?.id).map((id) => {
            const person = employees.find((e) => e.id === id);
            return (
              <button key={id} type="button" onClick={() => setParticipantIds((ids) => ids.filter((x) => x !== id))}
                className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-100">
                {person?.name}<X className="h-3 w-3" />
              </button>
            );
          })}
        </div>
        <input value={peopleQuery} onChange={(e) => setPeopleQuery(e.target.value)} placeholder="Ajouter un collègue…"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-300" />
        {peopleQuery && (
          <div className="mt-1 rounded-xl border border-slate-100">
            {peopleMatches.map((e) => (
              <button key={e.id} type="button" onClick={() => { setParticipantIds((ids) => [...ids, e.id]); setPeopleQuery(''); }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50">{e.name}</button>
            ))}
          </div>
        )}
        <p className="mb-4 mt-1 text-xs text-slate-400">Les participants pourront lire le transcript complet ; la synthèse est visible de tous.</p>

        <label className="mb-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={hideTranscript} onChange={(e) => setHideTranscript(e.target.checked)} className="mt-1" />
          <span>Pas de transcript<span className="block text-xs text-slate-500">Seule la synthèse sera affichée sur la réunion.</span></span>
        </label>

        {/* Plus de case à cocher : lancer l'enregistrement vaut accord des personnes présentes. */}
        <p className="mb-5 flex items-start gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
          En lançant l'enregistrement, tu confirmes que les personnes présentes savent que la réunion est enregistrée et
          transcrite.
        </p>

        <button type="submit" disabled={!canStart}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white shadow hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
          <span className="h-2.5 w-2.5 rounded-full bg-white" /> Démarrer l'enregistrement
        </button>
      </form>
    </div>
  );
};

export default StartMeetingDialog;
